import React, { useEffect, useState } from 'react';
import _debounce from 'lodash/debounce';

import styles from './Organizations.module.css';
import { TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import svg from '@icons/Search.svg';
import OrganizationsTable from './OrganizationsTable';
import PlacesTable from "./PlacesTable";
import RequestsTable from './RequestsTable';
import useClaims from './hooks/useClaims';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useOrganizations from './hooks/useOrganizations';
import usePlaces from "./hooks/usePlaces"
import { notifications } from "@mantine/notifications";

const Places = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  let pageInitial = 'requests';
  let searchValue = ''

  for (const entry of searchParams.entries()) {
    const [param, value] = entry;

    if (param === 'page') {
      pageInitial = value
    }

    if (param === 'search') {
      searchValue = value
    }
  }

  const [search, setSearch] = useState(searchValue);
  const [debouceSearch, setDebounceSearch] = useState(searchValue);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(pageInitial);

  const [
    {
      organizations,
      sortName: sortOrganizationsName,
      sortValue: sortOrganizationsValue,
    },
    {
      fetchOrganizations,
      sort: sortOrganizations,
      filterOrganizations,
    }
  ] = useOrganizations(t, navigate, searchParams);

  const [
    {
      claims,
      sortName,
      sortValue
    },
    {
      fetchClaims,
      filterClaims,
      updateClaim,
      fetchClaim,
      sort: sortClaims
    }
  ] = useClaims(t, navigate, searchParams);


  const [
    {
      places,
      sortName: sortPlaceName,
      sortValue: sortPlaceValue,
    },
    {
      fetchPlaces,
      filterPlaces,
      sort: sortPlaces
    }
  ] = usePlaces(t, navigate, searchParams);

  useEffect(() => {
    if (page === 'requests') {
      filterClaims(debouceSearch);
    } else if (page === 'places') {
      filterPlaces(debouceSearch)
    } else {
      filterOrganizations(debouceSearch);
    }
  }, [debouceSearch, places.length, claims.length, organizations.length])

  const handleChange = e => {
    const value = e.target.value;
    setDebounceSearch(value);

    let queryName = 'status'
    let queryValue = '-1'

    for (const entry of searchParams.entries()) {
      const [param, value] = entry;
  
      if (param === 'sortName') {
        queryName = value
      }
  
      if (param === 'sortValue') {
        queryValue = Number(value)
      }
    }

    const queryValueString = queryValue ? `sortValue=${queryValue}` : '';
    const queryNameString = queryName ? `sortName=${queryName}` : '';
    const pageQuery = page ? `page=${page}` : '';
    const searchString = value ? `search=${value}` : '';
    const queryString = [queryValueString, pageQuery, queryNameString, searchString]
      .filter(a => !!a)
      .join('&');

    navigate(`/organizations/${!!queryString ? '?' : ''}${queryString}`, { replace: true })
  }

  const handleChangeDebounced = e => {
    setSearch(e.target.value)
    _debounce(() => handleChange(e), 200)()
  }

  const icon = <img src={svg} />

  const handleTabClick = (value) => {
    navigate(`/organizations?page=${value}`)
    setPage(value);
    setSearch('');
    setDebounceSearch('');
  }

  useEffect(() => {
    setLoading(false)
    if (page === 'requests') {
      claimsLoader();
    } else if (page === 'places') {
      placesLoader();
    } else {
      organizationsLoader()
    }
  }, [page])


  const placesLoader = async () => {
    load(fetchPlaces())
      .catch((er) => {
        notifications.show({
          title: JSON.stringify(er),
          autoClose: 2000
        })
      });
  }

  const claimsLoader = async () => {
    load(fetchClaims())
      .catch((er) => {
        notifications.show({
          title: JSON.stringify(er),
          autoClose: 2000
        })
      });
  }


  const organizationsLoader = async () => {
    load(fetchOrganizations())
      .catch((er) => {
        notifications.show({
          title: JSON.stringify(er),
          autoClose: 2000
        })
      });
  }

  const load = async (f) => {
    setLoading(true);
    f.then(() => setLoading(false))
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.top}>
        <div
          className={cn(styles.tabTitle, page === 'requests' && styles.active)}
          onClick={() => handleTabClick('requests')}
        >
          {t('requests')}
        </div>

        <div
          className={cn(styles.tabTitle, page === 'organizations' && styles.active)}
          onClick={() => handleTabClick('organizations')}
        >
          {t('organizations')}
        </div>

        <div
          className={cn(styles.tabTitle, page === 'places' && styles.active)}
          onClick={() => handleTabClick('places')}
        >
          {t('places')}
        </div>
      </div>

      <div className={styles.searchWrapper}>
        <TextInput
          leftSection={icon}
          className={styles.search}
          onChange={handleChangeDebounced}
          placeholder={t('search')}
          value={search}
        />
      </div>

      <div className={styles.tableWrapper}>
        {(page === 'requests') && (
          <RequestsTable
            updateClaim={updateClaim}
            fetchClaim={fetchClaim}
            sort={sortClaims}
            search={search}
            isLoading={loading}
            fetchClaims={fetchClaims}
            sortName={sortName}
            sortValue={sortValue}
            claims={claims}
          />
        )}

        {(page === 'places') && (
          <PlacesTable
            places={places}
            sort={sortPlaces}
            search={search}
            isLoading={loading}
            sortName={sortPlaceName}
            sortValue={sortPlaceValue}
          />
        )}

        {(page === 'organizations') && (
          <OrganizationsTable
            organizations={organizations}
            sort={sortOrganizations}
            isLoading={loading}
            search={search}
            sortName={sortOrganizationsName}
            sortValue={sortOrganizationsValue}
          />
        )
        }
      </div>
    </div>
  )
}

export default Places;
